import React from "react";
import Box from "@mui/material/Box";
import Snackbar from "@mui/material/Snackbar";
import Grow from "@mui/material/Grow";
import { useSelector, useDispatch } from "react-redux";
import {
  getIsNotificationOpen,
  getNotificationMessage,
  getNotificationLevel,
  hideNotification,
} from "./notificationsSlice";

/**
 * Toast, built to the design system's `.neura-toast` (assets/neura.css).
 *
 * Was a bottom-left filled MUI Alert. The design puts it top-right on
 * `--surface-2` with a 3px left border carrying the level.
 *
 * MUI's Snackbar is kept as the mechanism — it brings the auto-hide timer,
 * the transition and `role="alert"` — but none of its Alert chrome is used.
 */

// 2200ms is the ttl the design passes to neura.toast() for these actions.
const AUTO_HIDE_MS = 2200;

const LEVEL_BORDER = {
  success: "var(--green)",
  info: "var(--blue)",
  error: "var(--red)",
};

export default function Notifications() {
  const dispatch = useDispatch();
  const open = useSelector(getIsNotificationOpen);
  const message = useSelector(getNotificationMessage);
  const level = useSelector(getNotificationLevel);

  const handleClose = (event, reason) => {
    // Leaving it up on click-away matches the design's behaviour: the toast is
    // informational and times out on its own.
    if (reason === "clickaway") return;
    dispatch(hideNotification());
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={AUTO_HIDE_MS}
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      TransitionComponent={Grow}
      sx={{ top: "16px !important", right: "16px !important", maxWidth: "calc(100vw - 32px)" }}
    >
      <Box
        role="alert"
        sx={{
          background: "var(--surface-2)",
          border: "1px solid var(--border-strong)",
          borderLeft: `3px solid ${LEVEL_BORDER[level] || "var(--accent)"}`,
          borderRadius: "var(--radius-md)",
          padding: "10px 14px",
          fontFamily: "var(--font-stack)",
          fontSize: "0.82rem",
          fontWeight: 600,
          color: "var(--text)",
          boxShadow: "var(--shadow-pop)",
          minWidth: "220px",
          maxWidth: "360px",
        }}
      >
        {message}
      </Box>
    </Snackbar>
  );
}
